/**
 * invoiceReader3.js
 *
 * AI-powered invoice reader for V3 bills.
 *
 * Uses Groq (llama-3.3-70b-versatile) to extract structured data from
 * raw invoice text — handles any vendor format without regex hacks.
 *
 * The agent:
 *   1. Receives raw text extracted from the PDF via pdfjs
 *   2. Sends it to Groq with a strict JSON extraction prompt
 *   3. Returns a normalised object: invoiceNumber, poNumber, dates, totals
 *   4. PO format "PROJ#-MO#" is split client-side after extraction
 *
 * Usage:
 *   import { extractInvoiceDataAI, matchVendorFromText } from "./invoiceReader3";
 *
 * IMPORTANT: Requires VITE_GROQ_API_KEY (or REACT_APP_GROQ_API_KEY) in .env
 * OR route through your backend at /api/ai/extract-invoice (recommended for
 * production so the key stays server-side).
 */

const GROQ_URL = "https://api.groq.com/openai/v1/chat/completions";
const GROQ_MODEL = "llama-3.3-70b-versatile";

// ─── Get API key — client env var or fall back to backend proxy ───────────────
function getGroqKey() {
  // Vite
  if (
    typeof import.meta !== "undefined" &&
    import.meta.env?.VITE_GROQ_API_KEY
  ) {
    return import.meta.env.VITE_GROQ_API_KEY;
  }
  // CRA
  if (typeof process !== "undefined" && process.env?.REACT_APP_GROQ_API_KEY) {
    return process.env.REACT_APP_GROQ_API_KEY;
  }
  return null;
}

// ─── Call Groq directly (client-side) ────────────────────────────────────────
async function callGroqDirect(systemPrompt, userContent) {
  const key = getGroqKey();
  if (!key) throw new Error("No GROQ API key available client-side");

  const resp = await fetch(GROQ_URL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${key}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      model: GROQ_MODEL,
      max_tokens: 512,
      temperature: 0,
      response_format: { type: "json_object" },
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userContent },
      ],
    }),
  });

  if (!resp.ok) {
    const err = await resp.text();
    throw new Error(`Groq ${resp.status}: ${err}`);
  }

  const data = await resp.json();
  const text = data.choices?.[0]?.message?.content || "{}";
  return JSON.parse(text);
}

// ─── Call via backend proxy (production — key stays server-side) ──────────────
async function callGroqViaBackend(rawText) {
  const token = localStorage.getItem("token");
  const resp = await fetch(
    "https://erp-production-72da01c8e651.herokuapp.com/api/ai/extract-invoice",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ text: rawText }),
    },
  );
  if (!resp.ok) {
    const err = await resp.text();
    throw new Error(`Backend proxy ${resp.status}: ${err}`);
  }
  const data = await resp.json();
  return data.extracted || {};
}

// ─── System prompt ────────────────────────────────────────────────────────────
const EXTRACTION_SYSTEM_PROMPT = `You are an invoice data extraction agent for a construction company.

Extract the following fields from invoice text and return ONLY a JSON object.
If a field cannot be found, use null.

JSON schema to return:
{
  "invoiceNumber": string | null,       // The vendor's invoice/reference number
  "poNumber": string | null,            // Customer PO number as shown on invoice (e.g. "87-839", "25003-1100", "85-678")
"invoiceDate": string | null,   // ISO YYYY-MM-DD; invoices use MM/DD/YYYY (month first); "05/15/2025" → "2025-05-15"
"dueDate": string | null,       // ISO YYYY-MM-DD; same MM/DD/YYYY rule
  "subtotal": number | null,            // Pre-tax amount
  "hst": number | null,                 // HST / GST / Sales Tax amount
  "total": number | null,               // Final invoice total (including tax)
  "vendorName": string | null,          // Company issuing the invoice
  "currency": "CAD" | "USD" | null
}

Rules:
- poNumber: look for labels like "P.O. Number", "Customer PO", "PO #", "Job No" — return the raw number exactly as shown
- invoiceNumber: look for "Invoice #", "Invoice No", "Invoice Number" — NOT the PO number
- dates: convert any date format to YYYY-MM-DD
- amounts: return as numbers (no $ sign, no commas)
- total must be >= subtotal + hst (approximately)
- If subtotal is missing but total and hst are present, derive subtotal = total - hst
- vendorName: the company sending the invoice (not the bill-to company)
- Return ONLY the JSON object, no explanation, no markdown`;

// ─── Main AI extractor ────────────────────────────────────────────────────────
/**
 * Extract invoice data using Groq AI.
 *
 * @param {string} rawText   Full text extracted from the PDF
 * @param {object} options   { useBackend: boolean }  default: tries backend, falls back to direct
 * @returns {Promise<object>} Normalised invoice fields
 */
export async function extractInvoiceDataAI(rawText, options = {}) {
  if (!rawText || rawText.trim().length < 20) {
    return emptyResult("Text too short to extract from");
  }

  // Truncate to ~6000 chars — enough for any invoice, avoids token waste
  const truncated = rawText.slice(0, 6000);

  let extracted = {};

  // Try backend proxy first (key stays server-side), then direct
  try {
    if (options.useBackend !== false) {
      extracted = await callGroqViaBackend(truncated);
    } else {
      throw new Error("Backend disabled");
    }
  } catch (backendErr) {
    console.warn(
      "[InvoiceReader] Backend proxy failed, trying direct:",
      backendErr.message,
    );
    try {
      extracted = await callGroqDirect(EXTRACTION_SYSTEM_PROMPT, truncated);
    } catch (directErr) {
      console.error(
        "[InvoiceReader] Direct Groq call also failed:",
        directErr.message,
      );
      return emptyResult(`AI extraction failed: ${directErr.message}`);
    }
  }

  return normalise(extracted);
}

const RECEIPT_SYSTEM_PROMPT = `You are reading a simple receipt, cash receipt, or delivery slip for a construction supply company.
The document may be handwritten, photographed, or a thermal print. Extract whatever financial data is visible.
Return ONLY a JSON object. Use null for missing fields.

{
  "invoiceNumber": string | null,
  "invoiceDate":   string | null,
  "dueDate":       string | null,
  "subtotal":      number | null,
  "hst":           number | null,
  "total":         number | null,
  "vendorName":    string | null,
  "poNumber":      string | null
}

Rules:
- invoiceNumber: any receipt #, order #, ticket #, transaction #, or confirmation number
- invoiceDate: convert any date format to YYYY-MM-DD
- If only ONE dollar amount is visible, put it in "total" and leave subtotal/hst null
- If a tax line exists labelled HST, GST, TAX, or TX → put the amount in "hst"
- subtotal: pre-tax amount; if unlabelled derive as total − hst
- vendorName: company or store name, usually at the top of the slip
- poNumber: look for a DIGITS-DIGITS pattern (e.g. "87-839", "25007-208") anywhere on the receipt, including inside a "Job Name", "PO#", "Job/PO", or customer-reference line — it is often embedded alongside other job description text rather than sitting on its own clearly labelled line. Extract just the number, ignoring surrounding words (e.g. "JOB NAME/PO#: Smith Bathroom 87-839" → "87-839")
- Strip dollar signs, commas, and spaces from all numeric values
- Return ONLY the JSON object. No markdown. No explanation.`;

export async function extractReceiptDataAI(text) {
  try {
    const response = await fetch(
      "https://api.groq.com/openai/v1/chat/completions",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_GROQ_API_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: "llama-3.3-70b-versatile",
          max_tokens: 400,
          temperature: 0,
          response_format: { type: "json_object" },
          messages: [
            { role: "system", content: RECEIPT_SYSTEM_PROMPT },
            { role: "user", content: text.slice(0, 8000) },
          ],
        }),
      },
    );

    if (!response.ok) return {};
    const data = await response.json();
    const raw = JSON.parse(data.choices?.[0]?.message?.content || "{}");

    return {
      invoiceNumber: raw.invoiceNumber
        ? String(raw.invoiceNumber).trim()
        : null,
      invoiceDate: raw.invoiceDate ? String(raw.invoiceDate).trim() : null,
      dueDate: raw.dueDate ? String(raw.dueDate).trim() : null,
      subtotal: raw.subtotal != null ? parseFloat(raw.subtotal) || null : null,
      hst: raw.hst != null ? parseFloat(raw.hst) || null : null,
      total: raw.total != null ? parseFloat(raw.total) || null : null,
      vendorName: raw.vendorName ? String(raw.vendorName).trim() : null,
      poNumber: raw.poNumber ? String(raw.poNumber).trim() : null,
    };
  } catch {
    return {};
  }
}

// ─── Normalise AI response ────────────────────────────────────────────────────
// ─── Normalise AI response ────────────────────────────────────────────────────
function normalise(raw) {
  const result = {
    invoiceNumber: str(raw.invoiceNumber),
    poNumber: str(raw.poNumber),
    projectNumber: null,
    moNumber: null,
    invoiceDate: dateStr(raw.invoiceDate),
    dueDate: dateStr(raw.dueDate),
    subtotal: num(raw.subtotal),
    hst: num(raw.hst),
    total: num(raw.total),
    vendorName: str(raw.vendorName),
    currency: raw.currency || "CAD",
    totalReconciled: false,
    error: null,
  };

  // Split PO number into project + MO parts
  // Format: "PROJ#-MO#"  e.g. "87-839", "25003-1100", "85-678"
  // Split PO number into project + MO parts
  // Normalize spaces around dash first: "25007 - 208" → "25007-208"
  if (result.poNumber) {
    result.poNumber = result.poNumber.replace(/\s*[-–]\s*/g, "-").trim();

    const clean = result.poNumber;
    const dash = clean.indexOf("-");
    if (dash > 0) {
      result.projectNumber = clean.slice(0, dash);
      result.moNumber = clean.slice(dash + 1);
    } else {
      // No dash found — model returned only one half of the PO number
      console.warn(
        `[InvoiceReader] poNumber "${result.poNumber}" has no dash — ` +
          `likely truncated (e.g. "25007" instead of "25007-208"). Clearing.`,
      );
      result.poNumber = null;
      result.projectNumber = null;
      result.moNumber = null;
    }
  }

  // ── Reconcile totals ────────────────────────────────────────────────
  // Tax-line numbers (subtotal, hst) are positioned far more reliably in
  // raw PDF text than the "Total" label, which can drift across columns
  // (e.g. Watson invoices stack Subtotal/Total labels apart from values,
  // and have stray coverage numbers like "5,760.00" floating in the text).
  // Strategy:
  //   1. Fill any single missing value from the other two.
  //   2. If all three exist but don't reconcile, trust subtotal + hst.
  const TOL = 0.02; // cents-rounding tolerance
  const s = result.subtotal;
  const h = result.hst;
  const t = result.total;

  if (s != null && h != null && t == null) {
    result.total = round(s + h);
  } else if (t != null && h != null && s == null) {
    result.subtotal = round(t - h);
  } else if (t != null && s != null && h == null) {
    const d = round(t - s);
    if (d > 0) result.hst = d;
  } else if (s != null && h != null && t != null) {
    const expected = round(s + h);
    if (Math.abs(expected - t) > TOL) {
      console.warn(
        `[InvoiceReader] Total mismatch: extracted total=${t}, ` +
          `subtotal(${s}) + hst(${h}) = ${expected}. Using ${expected}.`,
      );
      result.total = expected;
      result.totalReconciled = true;
    }
  }

  return result;
}

function emptyResult(error) {
  return {
    invoiceNumber: null,
    poNumber: null,
    projectNumber: null,
    moNumber: null,
    invoiceDate: null,
    dueDate: null,
    subtotal: null,
    hst: null,
    total: null,
    vendorName: null,
    currency: "CAD",
    totalReconciled: false,
    error,
  };
}

function str(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  // The model sometimes returns the string "null" instead of JSON null
  if (s === "null" || s === "undefined" || s === "N/A" || s === "n/a")
    return null;
  return s.length > 0 ? s : null;
}

function num(v) {
  if (v === null || v === undefined) return null;
  const n = parseFloat(String(v).replace(/[$,]/g, ""));
  return isNaN(n) ? null : round(n);
}

function round(n) {
  return Math.round(n * 100) / 100;
}

function dateStr(v) {
  if (!v) return null;
  const s = String(v).trim();
  // Already ISO-shaped — validate month is real
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [year, mm, dd] = s.split("-");
    const month = parseInt(mm, 10);
    const day = parseInt(dd, 10);
    // Impossible month but valid day → AI swapped them
    if (month > 12 && day <= 12) {
      return `${year}-${dd}-${mm}`;
    }
    return s;
  }
  // Non-ISO string — let JS parse it
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    return d.toISOString().slice(0, 10);
  }
  return null;
}

// ─── Vendor fuzzy matcher (unchanged — no AI needed here) ────────────────────
/**
 * Fuzzy-match invoice text against known vendors by word overlap.
 *
 * @param {string} text       Full invoice text
 * @param {string} vendorName Vendor name extracted by AI (may be null)
 * @param {Array}  vendors    [{_id, companyName}]
 * @returns {string|null}     Vendor _id or null
 */
export function matchVendorFromText(text, vendorName, vendors) {
  if (!vendors || vendors.length === 0) return null;

  // Replace the norm function with this version that extracts dba names:
  function norm(s) {
    // Extract dba/d.b.a. alias first — "Titan GMS dba Watson Building Supplies" → "Watson Building Supplies"
    const dba = (s || "").match(/\bd\.?b\.?a\.?\s+"?([^"]+)"?/i);
    const base = dba ? dba[1] : s || "";
    return base
      .toLowerCase()
      .replace(/\binc\.?\b|\bltd\.?\b|\bllc\.?\b|\bcorp\.?\b|\bco\.?\b/g, "")
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  const textN = norm(text || "");
  let bestId = null,
    bestScore = 0;

  for (const v of vendors) {
    const words = norm(v.companyName)
      .split(" ")
      .filter((w) => w.length > 2);
    if (!words.length) continue;
    const hits = words.filter((w) => textN.includes(w)).length;
    const score = hits / words.length;
    if (score > bestScore && score >= 0.5) {
      bestScore = score;
      bestId = v._id;
    }
  }

  // Fallback: direct substring on extracted vendor name
  if (!bestId && vendorName) {
    const vn = norm(vendorName);
    for (const v of vendors) {
      const cn = norm(v.companyName);
      if (cn.includes(vn) || vn.includes(cn)) {
        bestId = v._id;
        break;
      }
    }
  }

  return bestId;
}
